import { type ReactNode, useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Search, ExternalLink, ChevronLeft, ChevronRight, X, BookOpen } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { Toc, type TocEntry } from './Toc'
import { nav, adjacentItems, findSection, findItem, flatNav } from '../nav'

// ── Search modal ───────────────────────────────────────────────────────────────

function SearchModal({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('')
  const navigate = useNavigate()
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => { ref.current?.focus() }, [])

  const results = q.trim().length > 1
    ? flatNav.filter(item => item.title.toLowerCase().includes(q.toLowerCase()))
    : []

  const go = (path: string) => { navigate(path); onClose() }

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            ref={ref}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search documentation…"
            className="flex-1 text-sm text-slate-900 outline-none placeholder-slate-400"
          />
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={16} />
          </button>
        </div>
        {results.length > 0 ? (
          <ul className="max-h-72 overflow-y-auto py-2">
            {results.map(item => {
              const section = nav.find(s => s.items.some(i => i.path === item.path))
              return (
                <li key={item.path}>
                  <button
                    onClick={() => go(item.path)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                  >
                    <span className="text-base">{section?.icon}</span>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{item.title}</p>
                      <p className="text-[11px] text-slate-400">{section?.title}</p>
                    </div>
                  </button>
                </li>
              )
            })}
          </ul>
        ) : q.trim().length > 1 ? (
          <p className="px-4 py-6 text-sm text-slate-400 text-center">No results for "{q}"</p>
        ) : (
          <div className="px-4 py-3">
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2">Quick links</p>
            {flatNav.slice(0, 6).map(item => (
              <button key={item.path} onClick={() => go(item.path)} className="block w-full text-left px-2 py-1.5 text-sm text-slate-600 hover:text-blue-600 hover:bg-slate-50 rounded transition-colors">
                {item.title}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Article wrapper ────────────────────────────────────────────────────────────

interface ArticleProps {
  children: ReactNode
  toc?: TocEntry[]
}

export function Article({ children, toc = [] }: ArticleProps) {
  const location = useLocation()
  const { prev, next } = adjacentItems(location.pathname)
  const item = findItem(location.pathname)
  const section = findSection(location.pathname)

  return (
    <div className="flex-1 flex min-w-0">
      <article className="flex-1 min-w-0 px-8 py-8 max-w-3xl">
        {/* Breadcrumb */}
        {section && (
          <div className="flex items-center gap-1.5 text-[12px] text-slate-400 mb-6">
            <span>{section.icon}</span>
            <span>{section.title}</span>
            <span>/</span>
            <span className="text-slate-600 font-medium">{item?.title}</span>
          </div>
        )}

        {/* Content */}
        {children}

        {/* Prev / Next navigation */}
        <div className="flex items-stretch gap-4 mt-12 pt-6 border-t border-slate-200">
          {prev ? (
            <Link to={prev.path} className="flex-1 flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all group">
              <ChevronLeft size={16} className="text-slate-400 group-hover:text-blue-600 shrink-0" />
              <div>
                <p className="text-[11px] text-slate-400 mb-0.5">Previous</p>
                <p className="text-sm font-medium text-slate-700 group-hover:text-blue-600">{prev.title}</p>
              </div>
            </Link>
          ) : <div className="flex-1" />}

          {next ? (
            <Link to={next.path} className="flex-1 flex items-center justify-end gap-3 p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 transition-all group text-right">
              <div>
                <p className="text-[11px] text-slate-400 mb-0.5">Next</p>
                <p className="text-sm font-medium text-slate-700 group-hover:text-blue-600">{next.title}</p>
              </div>
              <ChevronRight size={16} className="text-slate-400 group-hover:text-blue-600 shrink-0" />
            </Link>
          ) : <div className="flex-1" />}
        </div>
      </article>

      <Toc entries={toc} />
    </div>
  )
}

// ── Root layout ────────────────────────────────────────────────────────────────

export function Layout({ children }: { children: ReactNode }) {
  const [searchOpen, setSearchOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true) }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-white">
      {/* Top bar */}
      <header className="h-12 border-b border-slate-200 bg-white flex items-center px-4 gap-4 shrink-0 z-20">
        {/* Brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0 w-[248px]">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-sm">
            <BookOpen size={14} className="text-white" />
          </div>
          <div>
            <span className="font-bold text-[14px] text-slate-900 leading-none">Crawl</span>
            <span className="text-[11px] text-slate-400 ml-1.5 leading-none">Rabbit Docs</span>
          </div>
        </Link>

        {/* Search */}
        <button
          onClick={() => setSearchOpen(true)}
          className="flex-1 max-w-sm flex items-center gap-2 bg-slate-100 border border-slate-200 rounded-lg px-3 h-8 cursor-pointer hover:border-slate-300 transition-colors text-left"
        >
          <Search size={13} className="text-slate-400 shrink-0" />
          <span className="text-[13px] text-slate-400 flex-1">Search docs…</span>
          <kbd className="hidden sm:inline text-[10px] text-slate-400 bg-white border border-slate-200 px-1 py-px rounded font-sans">Ctrl K</kbd>
        </button>

        {/* Back to Console */}
        <a
          href="http://localhost:3004"
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto flex items-center gap-1.5 text-[12.5px] text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors shrink-0"
        >
          Back to Console
          <ExternalLink size={12} className="text-slate-400" />
        </a>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto ml-[260px]">
          {children}
        </main>
      </div>

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </div>
  )
}
